export const DASHBOARD_VIEWS = {
  MEMBER: 'member',
  OWNER: 'owner',
  VALIDATION: 'validation',
  ANALYTICS: 'analytics',
};

export const DASHBOARD_VIEW_OPTIONS = [
  { value: DASHBOARD_VIEWS.MEMBER, label: 'Mes questions' },
  { value: DASHBOARD_VIEWS.OWNER, label: 'MSL' },
  { value: DASHBOARD_VIEWS.VALIDATION, label: 'Validations' },
  { value: DASHBOARD_VIEWS.ANALYTICS, label: 'Statistiques' },
];

const MSL_DIVISIONS = ['Médical'];
const MEMBER_TILES_DIVISIONS = ['DAP', 'Ventes', 'Marché', 'Marchés'];
const LEGACY_ADMIN_DIVISIONS = ['Marketing', 'Finances', 'RH', 'Office'];
const MEDICAL_ADMIN_DIVISIONS = ['DAP', 'Médical'];

function hasDivision(divisions = [], allowed) {
  return divisions.some((division) => allowed.includes(division));
}

export function getAccessibleDashboardViews({
  role,
  divisions = [],
  isValidator = false,
} = {}) {
  if (role === 'Member') {
    return [DASHBOARD_VIEWS.MEMBER];
  }

  if (role === 'Owner') {
    const views = [];
    if (hasDivision(divisions, MSL_DIVISIONS)) {
      views.push(DASHBOARD_VIEWS.OWNER);
    }
    if (hasDivision(divisions, MEMBER_TILES_DIVISIONS)) {
      views.push(DASHBOARD_VIEWS.MEMBER);
    }
    if (hasDivision(divisions, LEGACY_ADMIN_DIVISIONS)) {
      views.push(DASHBOARD_VIEWS.ANALYTICS);
    }
    if (views.length === 0) {
      return [DASHBOARD_VIEWS.MEMBER];
    }
    return views;
  }

  if (role === 'Admin') {
    const views = [
      DASHBOARD_VIEWS.MEMBER,
      DASHBOARD_VIEWS.OWNER,
    ];
    if (isValidator) {
      views.push(DASHBOARD_VIEWS.VALIDATION);
    }
    views.push(DASHBOARD_VIEWS.ANALYTICS);
    return views;
  }

  return [DASHBOARD_VIEWS.ANALYTICS];
}

function getFirstNonAnalyticsView(views = []) {
  return views.find((view) => view !== DASHBOARD_VIEWS.ANALYTICS) || DASHBOARD_VIEWS.MEMBER;
}

export function getSwitcherDashboardViews(views = []) {
  return views.filter((view) => view !== DASHBOARD_VIEWS.ANALYTICS);
}

export function getDefaultDashboardView({
  role,
  divisions = [],
  isValidator = false,
  hasPendingValidation = false,
  accessibleViews,
} = {}) {
  const accessible = accessibleViews
    || getAccessibleDashboardViews({ role, divisions, isValidator });

  if (
    isValidator
    && hasPendingValidation
    && accessible.includes(DASHBOARD_VIEWS.VALIDATION)
  ) {
    return DASHBOARD_VIEWS.VALIDATION;
  }

  if (role === 'Member') {
    return DASHBOARD_VIEWS.MEMBER;
  }

  if (role === 'Owner') {
    if (hasDivision(divisions, MSL_DIVISIONS) && accessible.includes(DASHBOARD_VIEWS.OWNER)) {
      return DASHBOARD_VIEWS.OWNER;
    }
    if (
      hasDivision(divisions, MEMBER_TILES_DIVISIONS)
      && accessible.includes(DASHBOARD_VIEWS.MEMBER)
    ) {
      return DASHBOARD_VIEWS.MEMBER;
    }
    return getFirstNonAnalyticsView(accessible);
  }

  if (role === 'Admin') {
    if (
      hasDivision(divisions, MEDICAL_ADMIN_DIVISIONS)
      && accessible.includes(DASHBOARD_VIEWS.OWNER)
    ) {
      return DASHBOARD_VIEWS.OWNER;
    }
    return DASHBOARD_VIEWS.MEMBER;
  }

  return getFirstNonAnalyticsView(accessible);
}

const STORAGE_KEY_PREFIX = 'medocta.dashboardView.';

function canPersistDashboardView(view) {
  return Boolean(view)
    && Object.values(DASHBOARD_VIEWS).includes(view)
    && view !== DASHBOARD_VIEWS.ANALYTICS;
}

export function getStoredDashboardView(userId) {
  if (!userId) {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
    return canPersistDashboardView(stored) ? stored : null;
  } catch (error) {
    return null;
  }
}

export function persistDashboardView(userId, view) {
  if (!userId || !canPersistDashboardView(view)) {
    return;
  }

  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, view);
  } catch (error) {}
}

export function resolveDashboardView({
  requestedView,
  storedView,
  role,
  divisions,
  isValidator,
  hasPendingValidation,
} = {}) {
  const accessibleViews = getAccessibleDashboardViews({
    role,
    divisions,
    isValidator,
  });
  const defaultView = getDefaultDashboardView({
    role,
    divisions,
    isValidator,
    hasPendingValidation,
    accessibleViews,
  });

  if (requestedView && accessibleViews.includes(requestedView)) {
    return { view: requestedView, accessibleViews };
  }

  if (
    isValidator
    && hasPendingValidation
    && accessibleViews.includes(DASHBOARD_VIEWS.VALIDATION)
  ) {
    return { view: DASHBOARD_VIEWS.VALIDATION, accessibleViews };
  }

  if (canPersistDashboardView(storedView) && accessibleViews.includes(storedView)) {
    return { view: storedView, accessibleViews };
  }

  return { view: defaultView, accessibleViews };
}
